import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useReducer, useEffect, useRef, useState } from "react";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";

import ContentContainer from "~/components/layout/ContentContainer";

import ImageLink from "~/components/links/ImageLink";

import IcoIcon from "~/components/IcoIcon";
import HeaderSearch from "~/components/Header/HeaderSearch";
import TopHeader from "~/components/Header/TopHeader";
import HeaderNav from "~/components/Header/HeaderNav";

import useOnClickOutside from "~/hooks/useOnClickOutside";

import transition from "~/utilities/transition";

import {
    mobileNavOpenFocusManagement,
    mobileNavCloseFocusManagement,
} from "~/utilities/accessibilityHelpers";

import { useStickyContext } from "~/context/StickyContext";

import { headerInitialState, headerReducer } from "~/reducers/header";

const StyledHeader = styled.header`
    display: flex;
    flex-direction: column;
    z-index: ${({ theme }) => theme.zIndex.header};
    position: ${({ sticky }) => (sticky ? "sticky" : "relative")};
    top: ${({ elementOffset }) =>
        elementOffset ? `${elementOffset}px` : `0px`};
`;

const MainHeader = styled.div`
    background-color: ${({ theme }) => theme.palette.darkBlue};
    min-height: 70px;
    display: flex;
    align-items: center;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        min-height: 90px;
    }
`;

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        justify-content: space-between;
    }
`;

const MobileLogo = styled.div`
    width: 100%;
    max-width: 200px;
    display: block;
    margin: 0 ${({ theme }) => theme.layout.gutter};

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        display: none;
        margin: 0;
    }
`;

const MobileNavWrapper = styled.div`
    padding: 10px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        justify-content: flex-end;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        display: none;
    }
`;

export const SearchButton = styled.button`
    font-weight: 500;
    font-size: 1.8rem;
    letter-spacing: 0.056em;
    color: ${({ theme }) => theme.palette.white};
    margin-right: 0;
    padding-bottom: 0;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        margin-right: ${({ theme }) => theme.layout.gutter};
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        ${transition({}, "border-bottom")};
        position: relative;
        margin-right: 0;

        &:after {
            content: "";
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            border-bottom: ${({ theme, isOpen }) =>
                isOpen ? `7px solid ${theme.palette.lightBlue}` : `none`};
        }
    }
`;

export const MobileMenuToggle = styled.button`
    font-weight: 500;
    font-size: 1.8rem;
    letter-spacing: 0.056em;
    color: ${({ theme, mobileMenuIsOpen }) =>
        mobileMenuIsOpen ? theme.palette.darkBlue : theme.palette.white};
`;

const Header = ({
    headerLogo = null,
    locationSiteLinks = {},
    topNav = [],
    mainNav = [],
    headerSearchOptions = [],
    headerNotSticky = false,
    languages = [],
}) => {
    const [state, dispatch] = useReducer(headerReducer, headerInitialState);

    const {
        exitBodyScrollLock,
        mobileMenuIsOpen,
        searchIsOpen,
        mobileSubNavIsOpen,
        openMainSubnav,
        openTopSubnav,
        openLangSubNav,
        openCountrySubNav,
    } = state;

    const mainHeaderRef = useRef();
    const mainNavRef = useRef();
    const searchWrapperRef = useRef();
    const [focusedElement, setFocusedElement] = useState(null);

    const { elementOffset, ref: stickyRef } = useStickyContext();

    const handleDispatch = (type, id, hasSubitems) => {
        dispatch({ type, id, hasSubitems });
    };

    useOnClickOutside(mainHeaderRef, () => {
        handleDispatch("HANDLE_CLOSE_ALL", "search", true);
    });

    useEffect(() => {
        if (exitBodyScrollLock) {
            clearAllBodyScrollLocks();
        }
    }, [exitBodyScrollLock]);

    useEffect(() => {
        if (mobileMenuIsOpen) {
            mobileNavOpenFocusManagement({ focusedElement, setFocusedElement });
            disableBodyScroll(mainNavRef.current);
        } else if (searchIsOpen) {
            disableBodyScroll(searchWrapperRef.current);
        } else {
            clearAllBodyScrollLocks();
        }
    }, [mobileMenuIsOpen, searchIsOpen, focusedElement, setFocusedElement]);

    return (
        <StyledHeader
            {...{ elementOffset }}
            ref={stickyRef}
            sticky={!headerNotSticky}
        >
            <TopHeader
                {...{
                    locationSiteLinks,
                    topNav,
                    openTopSubnav,
                    openLangSubNav,
                    openCountrySubNav,
                    dispatch,
                    mobileMenuIsOpen,
                    languages,
                }}
            />

            <MainHeader ref={mainHeaderRef}>
                <ContentContainer>
                    <Wrapper>
                        <HeaderNav
                            {...{
                                headerLogo,
                                topNav,
                                mainNav,
                                mobileMenuIsOpen,
                                mobileSubNavIsOpen,
                                handleDispatch,
                                mobileNavCloseFocusManagement,
                                openMainSubnav,
                                mainNavRef,
                                languages,
                                locationSiteLinks,
                            }}
                        />

                        <MobileNavWrapper>
                            <SearchButton
                                onClick={() =>
                                    handleDispatch("HANDLE_SEARCH_TOGGLE")
                                }
                                type="button"
                                aria-expanded={openMainSubnav === "search"}
                                aria-label="Search"
                            >
                                <IcoIcon icon="search" />
                            </SearchButton>
                            <MobileLogo>
                                {headerLogo ? (
                                    <ImageLink to="/" image={headerLogo} />
                                ) : null}
                            </MobileLogo>
                            <MobileMenuToggle
                                data-label="mobileMenuToggle"
                                onClick={() =>
                                    handleDispatch("HANDLE_MOBILE_MENU_TOGGLE")
                                }
                                type="button"
                                aria-label="Open mobile navigation"
                            >
                                <IcoIcon icon="toggle" />
                            </MobileMenuToggle>
                        </MobileNavWrapper>
                        <HeaderSearch
                            isOpen={searchIsOpen}
                            onClick={() =>
                                handleDispatch("HANDLE_SEARCH_CLOSE")
                            }
                            {...{ headerSearchOptions, searchWrapperRef }}
                        />
                    </Wrapper>
                </ContentContainer>
            </MainHeader>
        </StyledHeader>
    );
};

Header.propTypes = {
    languages: PropTypes.array,
    headerLogo: PropTypes.shape({
        sourceUrl: PropTypes.string,
        altText: PropTypes.string,
        title: PropTypes.string.isRequired,
    }),
    locationSiteLinks: PropTypes.shape({
        currentLocation: PropTypes.string.isRequired,
        additionalSiteLocations: PropTypes.arrayOf(
            PropTypes.shape({
                linkLabel: PropTypes.string.isRequired,
                linkUrl: PropTypes.string.isRequired,
            }),
        ),
    }),
    topNav: PropTypes.arrayOf(
        PropTypes.shape({
            linkLabel: PropTypes.string.isRequired,
            linkUrl: PropTypes.string.isRequired,
            subNavigationItems: PropTypes.arrayOf(
                PropTypes.shape({
                    linkUrl: PropTypes.string.isRequired,
                    linkLabel: PropTypes.string.isRequired,
                }),
            ),
        }),
    ),
    mainNav: PropTypes.arrayOf(
        PropTypes.shape({
            linkLabel: PropTypes.string.isRequired,
            linkUrl: PropTypes.string.isRequired,
            singleColumnSubNavigation: PropTypes.bool,
            type: PropTypes.oneOf([
                "links",
                "cards",
                "iconLinks",
                "noSubNavigation",
            ]).isRequired,
            links: PropTypes.arrayOf(
                PropTypes.shape({
                    linkLabel: PropTypes.string.isRequired,
                    linkUrl: PropTypes.string.isRequired,
                    subNavigation: PropTypes.arrayOf(
                        PropTypes.shape({
                            linkUrl: PropTypes.string.isRequired,
                            linkLabel: PropTypes.string.isRequired,
                        }),
                    ),
                }),
            ),
            cards: PropTypes.arrayOf(
                PropTypes.shape({
                    logo: PropTypes.shape({
                        sourceUrl: PropTypes.string,
                        altText: PropTypes.string,
                        title: PropTypes.string.isRequired,
                    }),
                    logoBackgroundColor: PropTypes.string,
                    title: PropTypes.string,
                    subTitle: PropTypes.string,
                    productsImage: PropTypes.shape({
                        sourceUrl: PropTypes.string,
                        altText: PropTypes.string,
                        title: PropTypes.string.isRequired,
                    }),
                    buttonLabel: PropTypes.string,
                    buttonUrl: PropTypes.string,
                    additionalLinks: PropTypes.arrayOf(
                        PropTypes.shape({
                            linkLabel: PropTypes.string.isRequired,
                            linkUrl: PropTypes.string.isRequired,
                        }),
                    ),
                }),
            ),
            iconLinks: PropTypes.arrayOf(
                PropTypes.shape({
                    iconName: PropTypes.string,
                    linkUrl: PropTypes.string.isRequired,
                    linkLabel: PropTypes.string.isRequired,
                }),
            ),
        }),
    ),
    headerSearchOptions: PropTypes.arrayOf(
        PropTypes.shape({
            text: PropTypes.string,
            value: PropTypes.string,
        }),
    ),
    headerNotSticky: PropTypes.bool,
};

export default Header;
